import { useEffect, useMemo, useState } from "react";
import { Loader2, Mails, Pencil, Plus, Trash2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    useCreateDistributionList,
    useDeleteDistributionList,
    useDistributionLists,
    useDlDomains,
    useUpdateDistributionList
} from "@/api/hooks";
import type { DistributionList } from "@/api/adminBff";

/**
 * Distribution lists (group addresses). Postfix expands the list address to
 * its members via virtual_alias_maps, so a change here is live for the very
 * next message — no restart, no sync delay. Members must be existing local
 * mailboxes (the BFF validates); external members are a later phase because
 * outbound relaying from a foreign envelope sender would fail SPF.
 */
export function DistributionListsPage() {
    const domains = useDlDomains();
    const [domainFilter, setDomainFilter] = useState<string>("");
    const [filter, setFilter] = useState("");
    const [editing, setEditing] = useState<DistributionList | "new" | null>(null);

    const lists = useDistributionLists(domainFilter || undefined);
    const del = useDeleteDistributionList();
    const [confirmDelete, setConfirmDelete] = useState<DistributionList | null>(null);

    const visible = useMemo(() => {
        const rows = lists.data ?? [];
        const q = filter.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(
            (l) =>
                l.email.includes(q) ||
                l.displayName.toLowerCase().includes(q) ||
                l.members.some((m) => m.includes(q))
        );
    }, [lists.data, filter]);

    return (
        <div className="mx-auto max-w-4xl">
            <header className="mb-4 flex items-center gap-2">
                <Mails className="h-6 w-6 text-primary" />
                <h1 className="text-2xl font-semibold">Distribution lists</h1>
                <Button size="sm" className="ml-auto" onClick={() => setEditing("new")}>
                    <Plus className="h-3.5 w-3.5" /> New list
                </Button>
            </header>
            <p className="mb-4 text-sm text-muted-foreground">
                A distribution list is a group address — mail sent to it is delivered to every
                member's mailbox. Changes apply to the next message immediately. Members must be
                existing mailboxes on this server.
            </p>

            <div className="mb-4 flex gap-2">
                <Input
                    type="search"
                    placeholder="Filter by address, name or member…"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="max-w-xs"
                />
                {(domains.data?.length ?? 0) > 1 ? (
                    <select
                        value={domainFilter}
                        onChange={(e) => setDomainFilter(e.target.value)}
                        className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        <option value="">All domains</option>
                        {(domains.data ?? []).map((d) => (
                            <option key={d} value={d}>@{d}</option>
                        ))}
                    </select>
                ) : null}
            </div>

            {lists.isPending ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading lists…
                </div>
            ) : lists.isError ? (
                <p className="text-sm text-destructive">{(lists.error as Error).message}</p>
            ) : visible.length === 0 ? (
                <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                    {filter ? "No lists match the filter." : "No distribution lists yet — create the first one."}
                </div>
            ) : (
                <ul className="space-y-3">
                    {visible.map((l) => (
                        <li key={l.id} className="rounded-lg border bg-card p-4 shadow-sm">
                            <div className="flex items-start gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-medium">{l.email}</span>
                                        {l.displayName ? (
                                            <span className="text-sm text-muted-foreground">{l.displayName}</span>
                                        ) : null}
                                        {!l.active ? (
                                            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                                                Disabled
                                            </span>
                                        ) : null}
                                    </div>
                                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                        <Users className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                                        {l.members.map((m) => (
                                            <span
                                                key={m}
                                                className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary"
                                            >
                                                {m}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex shrink-0 gap-1">
                                    <Button variant="ghost" size="sm" onClick={() => setEditing(l)} aria-label={`Edit ${l.email}`}>
                                        <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setConfirmDelete(l)}
                                        aria-label={`Delete ${l.email}`}
                                    >
                                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                    </Button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {editing ? (
                <ListDialog
                    list={editing === "new" ? null : editing}
                    domains={domains.data ?? []}
                    onClose={() => setEditing(null)}
                />
            ) : null}

            {confirmDelete ? (
                <ConfirmDeleteDialog
                    list={confirmDelete}
                    pending={del.isPending}
                    onConfirm={() =>
                        del.mutate(confirmDelete.id, { onSuccess: () => setConfirmDelete(null) })
                    }
                    onClose={() => setConfirmDelete(null)}
                />
            ) : null}
        </div>
    );
}

/** Create / edit dialog. The address is immutable after create (Postfix keys
 *  on it) — rename = delete + recreate, same as every mail platform. */
function ListDialog({
    list,
    domains,
    onClose
}: {
    list: DistributionList | null;
    domains: string[];
    onClose: () => void;
}) {
    const create = useCreateDistributionList();
    const update = useUpdateDistributionList();

    const [localPart, setLocalPart] = useState("");
    const [domain, setDomain] = useState(domains[0] ?? "");
    const [displayName, setDisplayName] = useState(list?.displayName ?? "");
    const [active, setActive] = useState(list?.active ?? true);
    const [membersText, setMembersText] = useState(list ? list.members.join("\n") : "");
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!domain && domains.length > 0) setDomain(domains[0]!);
    }, [domains, domain]);

    const pending = create.isPending || update.isPending;

    const submit = () => {
        const members = membersText
            .split(/[\n,;]+/)
            .map((m) => m.trim())
            .filter(Boolean);
        const onError = (e: Error) => setError(e.message);
        if (list) {
            update.mutate(
                { id: list.id, payload: { displayName, active, members } },
                { onSuccess: onClose, onError }
            );
        } else {
            create.mutate(
                { email: `${localPart.trim()}@${domain}`, displayName, members },
                { onSuccess: onClose, onError }
            );
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={list ? `Edit ${list.email}` : "New distribution list"}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={onClose}
        >
            <div
                className="w-full max-w-lg rounded-lg border bg-background p-5 shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="mb-4 text-base font-semibold">
                    {list ? `Edit ${list.email}` : "New distribution list"}
                </h2>

                {!list ? (
                    <div className="mb-3">
                        <label className="mb-1 block text-xs font-medium" htmlFor="dl-address">Address</label>
                        <div className="flex items-center gap-1">
                            <Input
                                id="dl-address"
                                placeholder="team"
                                value={localPart}
                                onChange={(e) => setLocalPart(e.target.value)}
                                className="flex-1"
                                autoFocus
                            />
                            <span className="text-sm text-muted-foreground">@</span>
                            <select
                                value={domain}
                                onChange={(e) => setDomain(e.target.value)}
                                className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                                {domains.map((d) => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                ) : null}

                <div className="mb-3">
                    <label className="mb-1 block text-xs font-medium" htmlFor="dl-name">Display name (optional)</label>
                    <Input
                        id="dl-name"
                        placeholder="Engineering team"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                    />
                </div>

                <div className="mb-3">
                    <label className="mb-1 block text-xs font-medium" htmlFor="dl-members">
                        Members — one address per line
                    </label>
                    <textarea
                        id="dl-members"
                        rows={6}
                        placeholder={"alice@example.com\nbob@example.com"}
                        value={membersText}
                        onChange={(e) => setMembersText(e.target.value)}
                        className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                        Each member must be an existing mailbox on this server.
                    </p>
                </div>

                {list ? (
                    <label className="mb-3 flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={active}
                            onChange={(e) => setActive(e.target.checked)}
                            className="h-4 w-4"
                        />
                        Active — mail to a disabled list is rejected
                    </label>
                ) : null}

                {error ? <p className="mb-3 text-sm text-destructive">{error}</p> : null}

                <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={onClose} disabled={pending}>
                        Cancel
                    </Button>
                    <Button size="sm" onClick={submit} disabled={pending || (!list && !localPart.trim())}>
                        {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                        {list ? "Save" : "Create"}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function ConfirmDeleteDialog({
    list,
    pending,
    onConfirm,
    onClose
}: {
    list: DistributionList;
    pending: boolean;
    onConfirm: () => void;
    onClose: () => void;
}) {
    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={`Delete ${list.email}`}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={onClose}
        >
            <div
                className="w-full max-w-md rounded-lg border bg-background p-5 shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="mb-2 text-base font-semibold">Delete {list.email}?</h2>
                <p className="mb-4 text-sm text-muted-foreground">
                    Mail sent to this address will be rejected as an unknown recipient. The
                    members' own mailboxes are not affected.
                </p>
                <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={onClose} disabled={pending}>
                        Cancel
                    </Button>
                    <Button variant="destructive" size="sm" onClick={onConfirm} disabled={pending}>
                        {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                        Delete
                    </Button>
                </div>
            </div>
        </div>
    );
}
